import { useState, useEffect } from 'react';
import { Row, Col, Table, Badge, Spinner, Button } from 'react-bootstrap';
import { ApiService } from '../services/api';
import { DeploymentStatus } from '../types';
import { getStatusColor, getStatusIcon, formatTimestamp, formatFullTimestamp } from '../utils/reporting';

export function Dashboard() {
  const [status, setStatus] = useState<DeploymentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  useEffect(() => {
    loadStatus();
    // Refresh every minute
    const interval = setInterval(() => {
      loadStatus();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    try {
      setError(null);
      const data = await ApiService.getDeploymentStatus();
      setStatus(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    loadStatus();
  };

  if (loading && !status) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading dashboard...</p>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="alert alert-danger">
        <h5>Error Loading Dashboard</h5>
        <p>{error}</p>
        <Button variant="outline-danger" size="sm" onClick={handleRefresh}>
          Retry
        </Button>
      </div>
    );
  }

  if (!status) {
    return <div className="alert alert-warning">No data available</div>;
  }

  return (
    <>
      {/* Header with refresh button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <small className="text-muted">
            Last updated: {formatFullTimestamp(lastRefresh.toISOString())}
          </small>
        </div>
        <Button 
          variant="outline-primary" 
          size="sm" 
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Refreshing...
            </>
          ) : (
            <>
              <i className="fas fa-sync-alt me-2"></i>
              Refresh
            </>
          )}
        </Button>
      </div>

      {/* Summary Cards - AdminLTE Info Boxes */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <div className="info-box">
            <span className="info-box-icon bg-info elevation-1"><i className="fas fa-cogs"></i></span>
            <div className="info-box-content">
              <span className="info-box-text">Total Workloads</span>
              <span className="info-box-number">{status.summary.total}</span>
            </div>
          </div>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <div className="info-box">
            <span className="info-box-icon bg-success elevation-1"><i className="fas fa-check-circle"></i></span>
            <div className="info-box-content">
              <span className="info-box-text">Deployed</span>
              <span className="info-box-number">{status.summary.deployed}</span>
            </div>
          </div>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <div className="info-box">
            <span className="info-box-icon bg-warning elevation-1"><i className="fas fa-clock"></i></span>
            <div className="info-box-content">
              <span className="info-box-text">Pending/Deploying</span>
              <span className="info-box-number">{status.summary.pending + status.summary.deploying}</span>
            </div>
          </div>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <div className="info-box">
            <span className="info-box-icon bg-danger elevation-1"><i className="fas fa-exclamation-circle"></i></span>
            <div className="info-box-content">
              <span className="info-box-text">Failed</span>
              <span className="info-box-number">{status.summary.failed}</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Workload Status Table - AdminLTE Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <i className="fas fa-list mr-2"></i>
            Workload Status
          </h3>
          <div className="card-tools">
            <span className="badge badge-secondary">{status.workloads.length} workloads</span>
          </div>
        </div>
        <div className="card-body p-0">
          {status.workloads.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-inbox fa-3x mb-3"></i>
              <p>No workloads deployed yet</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover className="mb-0">
                <thead>
                  <tr>
                    <th>Workload</th>
                    <th>Status</th>
                    <th>Last Deployed</th>
                    <th>Deployed By</th>
                    <th>Resources</th>
                  </tr>
                </thead>
                <tbody>
                  {status.workloads.map((workload) => (
                    <tr key={workload.name}>
                      <td>
                        <strong>{workload.name}</strong>
                      </td>
                      <td>
                        <Badge bg={getStatusColor(workload.status)}>
                          {getStatusIcon(workload.status)} {workload.status}
                        </Badge>
                        {workload.error && (
                          <div className="small text-danger mt-1">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {workload.error}
                          </div>
                        )}
                      </td>
                      <td>
                        <span title={formatFullTimestamp(workload.lastDeployed)}>
                          {formatTimestamp(workload.lastDeployed)}
                        </span>
                      </td>
                      <td>
                        <small>{workload.deployedBy || '-'}</small>
                      </td>
                      <td>
                        {workload.resources ? (
                          <small className="text-muted">
                            {workload.resources.vpc && (
                              <>
                                <i className="fas fa-network-wired me-1"></i>
                                {workload.resources.vpc}
                                <br />
                              </>
                            )}
                            {workload.resources.subnets !== undefined && (
                              <>
                                <i className="fas fa-layer-group me-1"></i>
                                {workload.resources.subnets} subnet{workload.resources.subnets !== 1 ? 's' : ''}
                                {workload.resources.securityGroups !== undefined && (
                                  <>, {workload.resources.securityGroups} SG{workload.resources.securityGroups !== 1 ? 's' : ''}</>
                                )}
                              </>
                            )}
                          </small>
                        ) : (
                          <small className="text-muted">-</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Data source info */}
      <div className="text-center mt-3">
        <small className="text-muted">
          Data source: {status.metadata.source} | 
          Cache TTL: {status.metadata.ttl}s | 
          Auto-refresh: Every 60s
        </small>
      </div>
    </>
  );
}
